import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import './Login.css';
import Button from 'Components/Button';
import Header from 'Components/Header';
import {Navigate} from 'react-router-dom';
import Input from 'Components/Input';
import Footer from 'Components/Footer';
import Alert from 'Components/Alert';
import Spinner from 'Components/Spinner';
import {fetchToken} from "../../redux/features/getToken";
import {useAppDispatch, useAppSelector} from "../../redux/store";
import {userLogin} from "../../redux/features/loginState";
import {userLoginFirebase} from "../../redux/features/userLoginFirebase";
import {signInWithPopup} from "firebase/auth";
import {auth, googleProvider, microsoftProvider, facebookProvider} from "../../firebase";
import { ReactComponent as GoogleIcon } from 'Resources/Icons/Google.svg';

const Login: React.FC = () => {
    const dispatch = useAppDispatch();
    const userlogin = useAppSelector((state) => state.userLogin);
    const prevUserLogin = useRef(userlogin);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [login, setLogin] = useState(false);
    const [error, setError] = useState('');
    const [visible, setVisible] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (prevUserLogin.current !== userlogin) {
            if (userlogin.status === 'failed') {
                const axiosError = userlogin.error.response as any;

                const status = axiosError?.response?.status;
                const needsRegistration = axiosError?.response?.data?.needs_registration;

                const isRegistrationRedirect = status === 403 && needsRegistration;

                if (isRegistrationRedirect) {
                    setIsLoading(false);
                } else {
                    const errorMessage = axiosError?.response?.data?.message || 'Ocurrió un error al iniciar sesión';
                    setError(errorMessage);
                    setIsLoading(false);
                    setVisible(true);
                }
            }
            if (userlogin.status === 'success') {
                setIsLoading(false);
                setLogin(true);
            }
            prevUserLogin.current = userlogin;
        }
    }, [userlogin]);

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleLoginClick();
        }
    };

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };


    const handleLoginClick = async () => {
        if (!email || !password) {
            setError('Por favor, completá ambos campos.');
            setVisible(true);
            return;
        }

        if (!isValidEmail(email)) {
            setError('Por favor, ingresá un correo electrónico válido.');
            setVisible(true);
            return;
        }

        if (password.length < 8) {
            setError('Contraseña no valida.');
            setVisible(true);
            return;
        }

        setIsLoading(true);
        await dispatch(fetchToken());
        await dispatch(userLogin({email, password}));
    };

    const handleSocialLogin = async (e: any, provider: any) => {
        try {
            if (e && e.preventDefault) {
                e.preventDefault();
            }

            setError('');
            setVisible(false);
            setIsLoading(true);

            const result = await signInWithPopup(auth, provider);
            const firebaseToken = await result.user.getIdToken();


            await dispatch(fetchToken());
            await dispatch(userLoginFirebase({token: firebaseToken})).unwrap();

            await auth.signOut();
            setIsLoading(false);

        } catch (err: any) {
            setIsLoading(false);

            const axiosError = err?.response as any;
            const status = axiosError?.status;
            const needsRegistration = axiosError?.data?.needs_registration;

            const isRegistrationRedirect = status === 403 && needsRegistration;

            if (isRegistrationRedirect) {
                navigate('/register', {
                    state: {prefillData: axiosError.data.prefill_data}
                });
            } else {

                if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
                    return;
                }
                const errorMessage = axiosError?.data?.message || 'Ocurrió un error al iniciar sesión';
                setError(errorMessage);
                setVisible(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onGoogleLogin = (e: any) => {
        e.preventDefault();
        handleSocialLogin(e, googleProvider);
    };

    const onFacebookLogin = (e: any) => {
        e.preventDefault();
        handleSocialLogin(e, facebookProvider);
    };

    const onMicrosoftLogin = (e: any) => {
        e.preventDefault();
        handleSocialLogin(e, microsoftProvider);
    };

    return (
        <div className="container not-logged index-page">
            {login && (
                <Navigate to="/home" replace={true}/>
            )}
            {isLoading && <Spinner/>}
            {error && <Alert isVisible={visible} type='error' setVisible={setVisible} text={error}/>}
            <Header type="login"/>
            <picture>
                <source media="(min-width: 900px)" srcSet={require('Resources/Images/banner-desktop-20240823.png')}/>
                <source media="(max-width: 899px)" srcSet={require('Resources/Images/banner-mobile-20240823.png')}/>
                <img src={require('Resources/Images/banner-mobile-20240823.png')} alt="" className="banner"/>
            </picture>
            <div className="main">

                <div className="login-container">
                    <form className="login-form">
                        <div className="form-group">
                            <Input
                                type="email"
                                id="email"
                                name="email"
                                className="input--w-100"
                                label=""
                                onChange={handleEmailChange}
                                placeholder="E-mail"
                                required={true}
                                showRequired={false}
                            />
                        </div>
                        <div className="form-group">
                            <Input
                                type="password"
                                id="password"
                                name="password"
                                className="input--w-100"
                                label=""
                                onChange={handlePasswordChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Contraseña"
                                required={true}
                                showRequired={false}
                            />
                        </div>

                        <Button name="INICIAR SESIÓN" type="login" onClick={handleLoginClick}/>

                        <Button
                            type="login"
                            name="Iniciar sesión con Google"
                            className="btn-google register-type"
                            icon={<GoogleIcon className="btn-google-icon" title="Icono de Google" />}
                            onClick={(e) => handleSocialLogin(e, googleProvider)}
                        />

                        <div style={{textAlign: "center", margin: "15px 0", color: "#666"}}>
                            <span>o</span>
                        </div>



                      {/* Botones de Facebook y Microsoft comentados para uso futuro
                      <Button
                          type="login"
                          name="Iniciar sesión con Facebook"
                          onClick={(e) => handleSocialLogin(e, facebookProvider)}
                      />

                      <Button
                          type="login"
                          name="Iniciar sesión con Microsoft"
                          onClick={(e) => handleSocialLogin(e, microsoftProvider)}
                      />
                      */}

                        <a href="/register" className="no-decoration"><Button type="create" name="CREAR CUENTA"/></a>

                    </form>
                </div>
                <div className="container-pass">
                    <a href="/forgot" className="pass"><u>Obtener / Recuperar mi contraseña</u></a>
                </div>
            </div>
            <Footer isFixed={true}/>
        </div>
    );
};

export default Login;